import * as Yup from "yup";
import i18next from "../../i18n";

/**
 * Values `EditUserForm` collects. `parent` holds a bare user id — or the empty-string sentinel
 * `EditUserForm.tsx` uses for "no responsible" (see that file's `NO_RESPONSIBLE_VALUE`) — never
 * the nested `{ id }` shape `PUT /user/{id}` actually expects. Reshaping happens in `onSubmit`,
 * the same boundary `NewUserFormValues` draws for its own `parent`
 * (see `newUserValidationSchema.ts`).
 */
export type EditUserFormValues = {
  firstName: string;
  lastName: string;
  phone: string;
  userCompanyType: string;
  parent: string;
};

/**
 * Validation for the edit-user form (`PUT /user/{id}`), extracted out of `EditUserForm.tsx` for
 * the same reason `newUserValidationSchema` was (Task 11) — testable without rendering the form.
 *
 * `canManageRole` is `EditUserForm.tsx`'s `canManageThisEdit`: true only when the record being
 * edited is NOT the caller themselves AND the caller is one who may manage people at all
 * (mirrors the backend's `UserMutationGuard.assertMayChangeRole` / `mayChangeRole`, which gates
 * both `userCompanyType` and `parent` together in `UserService.updateUser`). Both the Role and
 * Responsible selects are rendered only when this is true — rendering either one anyway, for a
 * self-edit or for a viewer who cannot manage anyone's role, would be a control that lies about
 * what it does: a "successful" submit that the backend silently drops. With the fields absent
 * there is nothing for a `.required()` rule to validate — enforcing one anyway would block a
 * legitimate profile edit (name/phone) behind a field the user is never shown.
 *
 * `parent` is never `.required()` in either branch: "no responsible" (the empty-string sentinel)
 * is a legitimate submission, not a missing one — see this task's brief on the previously-missing
 * clear-Responsible path.
 */
export const editUserValidationSchema = (canManageRole: boolean) =>
  Yup.object().shape({
    firstName: Yup.string().required(i18next.t("validations.firstName")),
    lastName: Yup.string().required(i18next.t("validations.lastName")),
    phone: Yup.string().required(i18next.t("validations.phoneNumber")),
    userCompanyType: canManageRole
      ? Yup.string().required(i18next.t("validations.userCompanyType"))
      : Yup.string(),
    parent: Yup.string(),
  });
